import { useEffect, useState } from "react";
import { Ban, CheckCircle2, FileDown, Plus, XCircle } from "lucide-react";
import { toast } from "sonner";

import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Loading, PageHeader } from "../../components/shared";

const DEFAULT_FORM = {
  code: "",
  name: "",
  category: "",
  description: "",
  location: "",
  capacity: "",
  active: true,
  resident_visible: true,
  booking_enabled: true,
  approval_required: false,
  slot_style: "TIME_RANGE",
  availability_windows: [
    {
      days: [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ],
      start_time: "09:00",
      end_time: "22:00",
    },
  ],
  rules: {
    slot_duration_minutes: 60,
    min_duration_minutes: 60,
    max_duration_minutes: 240,
    buffer_minutes: 0,
    max_advance_days: 90,
    min_notice_minutes: 0,
    allow_same_day: true,
    cancellation_allowed: true,
    cancellation_cutoff_hours: 0,
    max_bookings_per_unit_per_day: 0,
    max_bookings_per_unit_per_month: 0,
    max_active_upcoming_bookings: 0,
    participant_limit: 0,
    eligible_relationships: ["OWNER", "JOINT_OWNER", "TENANT"],
  },
  terms: "",
  instructions: "",
  fee_metadata: {
    fee_applicable: false,
    fee_amount: 0,
    deposit_applicable: false,
    deposit_amount: 0,
  },
};
const EMPTY_STATE_CLASS = [
  "border border-dashed border-slate-300 bg-white p-5",
  "text-sm text-slate-600",
].join(" ");

function Status({ value }) {
  const colors = {
    CONFIRMED: "bg-emerald-100 text-emerald-800",
    PENDING_APPROVAL: "bg-amber-100 text-amber-800",
    REJECTED: "bg-rose-100 text-rose-800",
    CANCELLED: "bg-slate-100 text-slate-700",
    COMPLETED: "bg-blue-100 text-blue-800",
  };
  const color = colors[value] || "bg-slate-100 text-slate-700";
  return (
    <span
      className={`rounded-full px-2 py-1 text-xs font-semibold ${color}`}
      data-testid={`amenity-status-${String(value).toLowerCase()}`}
    >
      {String(value || "UNKNOWN").replaceAll("_", " ")}
    </span>
  );
}

export default function SocietyAmenities() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [editing, setEditing] = useState("");
  const [tab, setTab] = useState("amenities");
  const [block, setBlock] = useState({
    amenity_id: "",
    start_local: "",
    end_local: "",
    reason: "",
    resident_message: "",
  });

  const load = async () => {
    try {
      const response = await api.get("/society-amenities/bootstrap");
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load amenities");
      setData({ amenities: [], bookings: [], blocks: [], units: [] });
    }
  };
  useEffect(() => {
    load();
  }, []);

  const saveAmenity = async () => {
    try {
      if (editing)
        await api.patch(`/society-amenities/amenities/${editing}`, form);
      else await api.post("/society-amenities/amenities", form);
      toast.success(editing ? "Amenity updated" : "Amenity created");
      setForm(DEFAULT_FORM);
      setEditing("");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save amenity");
    }
  };

  const saveBlock = async () => {
    try {
      await api.post("/society-amenities/blocks", block);
      toast.success("Blocked period saved");
      setBlock({
        amenity_id: "",
        start_local: "",
        end_local: "",
        reason: "",
        resident_message: "",
      });
      await load();
    } catch (error) {
      if (
        error.response?.status === 409 &&
        window.confirm(`${error.response.data.detail}. Continue?`)
      ) {
        await api.post("/society-amenities/blocks", {
          ...block,
          acknowledge_existing_bookings: true,
        });
        await load();
      } else
        toast.error(
          error.response?.data?.detail || "Could not block this period",
        );
    }
  };

  const action = async (bookingId, type) => {
    const prompt =
      type === "reject"
        ? "Resident-visible rejection reason"
        : type === "cancel"
          ? "Cancellation reason"
          : "Approval note (optional)";
    const value = window.prompt(prompt);
    if (value === null) return;
    try {
      const payload =
        type === "reject"
          ? { reason: value }
          : type === "cancel"
            ? { reason: value }
            : { note: value };
      await api.post(
        `/society-amenities/bookings/${bookingId}/${type}`,
        payload,
      );
      toast.success(`Booking ${type}d`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Booking update failed");
    }
  };

  if (!data) return <Loading />;
  const setRule = (key, value) =>
    setForm({ ...form, rules: { ...form.rules, [key]: value } });
  return (
    <div data-testid="society-amenities-page">
      <PageHeader
        title="Amenities & Facility Booking"
        subtitle={
          "Configure shared spaces, resident reservations, approvals, and operational availability."
        }
      />
      <div
        className="mb-6 flex flex-wrap gap-2"
        data-testid="amenity-workspace-tabs"
      >
        {[
          ["amenities", "Amenity Master"],
          ["blocks", "Blocked Periods"],
          ["bookings", "Bookings"],
          ["reports", "Reports"],
        ].map(([value, label]) => (
          <Button
            data-testid={`amenity-tab-${value}`}
            key={value}
            onClick={() => setTab(value)}
            size="sm"
            variant={tab === value ? "default" : "outline"}
          >
            {label}
          </Button>
        ))}
      </div>
      {tab === "amenities" && (
        <section className="grid gap-6 xl:grid-cols-[440px_1fr]">
          <div
            className="border bg-white p-5"
            data-testid="amenity-master-form"
          >
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-display text-xl font-semibold">
                {editing ? "Edit amenity" : "New amenity"}
              </h2>
              {editing && (
                <Button
                  data-testid="amenity-reset-form"
                  onClick={() => {
                    setForm(DEFAULT_FORM);
                    setEditing("");
                  }}
                  size="sm"
                  variant="outline"
                >
                  New
                </Button>
              )}
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Input
                data-testid="amenity-code-input"
                onChange={(event) =>
                  setForm({ ...form, code: event.target.value.toUpperCase() })
                }
                placeholder="CODE"
                value={form.code}
              />
              <Input
                data-testid="amenity-name-input"
                onChange={(event) =>
                  setForm({ ...form, name: event.target.value })
                }
                placeholder="Amenity name"
                value={form.name}
              />
              <Input
                data-testid="amenity-category-input"
                onChange={(event) =>
                  setForm({ ...form, category: event.target.value })
                }
                placeholder="Category"
                value={form.category}
              />
              <Input
                data-testid="amenity-location-input"
                onChange={(event) =>
                  setForm({ ...form, location: event.target.value })
                }
                placeholder="Location"
                value={form.location}
              />
              <Input
                data-testid="amenity-capacity-input"
                min="0"
                onChange={(event) =>
                  setForm({ ...form, capacity: event.target.value })
                }
                placeholder="Capacity"
                type="number"
                value={form.capacity}
              />
              <select
                className="rounded-md border border-slate-300 px-3 py-2"
                data-testid="amenity-slot-style"
                onChange={(event) =>
                  setForm({ ...form, slot_style: event.target.value })
                }
                value={form.slot_style}
              >
                <option value="TIME_RANGE">Time range</option>
                <option value="FIXED_SLOT">Fixed slots</option>
              </select>
            </div>
            <Textarea
              className="mt-3"
              data-testid="amenity-description-input"
              onChange={(event) =>
                setForm({ ...form, description: event.target.value })
              }
              placeholder="Resident description"
              value={form.description}
            />
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="text-sm">
                Opening time
                <Input
                  className="mt-1"
                  data-testid="amenity-opening-time"
                  onChange={(event) =>
                    setForm({
                      ...form,
                      availability_windows: [
                        {
                          ...form.availability_windows[0],
                          start_time: event.target.value,
                        },
                      ],
                    })
                  }
                  type="time"
                  value={form.availability_windows[0].start_time}
                />
              </label>
              <label className="text-sm">
                Closing time
                <Input
                  className="mt-1"
                  data-testid="amenity-closing-time"
                  onChange={(event) =>
                    setForm({
                      ...form,
                      availability_windows: [
                        {
                          ...form.availability_windows[0],
                          end_time: event.target.value,
                        },
                      ],
                    })
                  }
                  type="time"
                  value={form.availability_windows[0].end_time}
                />
              </label>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="text-sm">
                Min. minutes
                <Input
                  className="mt-1"
                  data-testid="amenity-min-duration"
                  onChange={(event) =>
                    setRule("min_duration_minutes", Number(event.target.value))
                  }
                  type="number"
                  value={form.rules.min_duration_minutes}
                />
              </label>
              <label className="text-sm">
                Max. minutes
                <Input
                  className="mt-1"
                  data-testid="amenity-max-duration"
                  onChange={(event) =>
                    setRule("max_duration_minutes", Number(event.target.value))
                  }
                  type="number"
                  value={form.rules.max_duration_minutes}
                />
              </label>
              <label className="text-sm">
                Slot minutes
                <Input
                  className="mt-1"
                  data-testid="amenity-slot-duration"
                  onChange={(event) =>
                    setRule("slot_duration_minutes", Number(event.target.value))
                  }
                  type="number"
                  value={form.rules.slot_duration_minutes}
                />
              </label>
              <label className="text-sm">
                Buffer minutes
                <Input
                  className="mt-1"
                  data-testid="amenity-buffer-duration"
                  onChange={(event) =>
                    setRule("buffer_minutes", Number(event.target.value))
                  }
                  type="number"
                  value={form.rules.buffer_minutes}
                />
              </label>
              <label className="text-sm">
                Advance days
                <Input
                  className="mt-1"
                  data-testid="amenity-advance-days"
                  onChange={(event) =>
                    setRule("max_advance_days", Number(event.target.value))
                  }
                  type="number"
                  value={form.rules.max_advance_days}
                />
              </label>
              <label className="text-sm">
                Cancellation cutoff hours
                <Input
                  className="mt-1"
                  data-testid="amenity-cancel-cutoff"
                  onChange={(event) =>
                    setRule(
                      "cancellation_cutoff_hours",
                      Number(event.target.value),
                    )
                  }
                  type="number"
                  value={form.rules.cancellation_cutoff_hours}
                />
              </label>
            </div>
            <Textarea
              className="mt-3"
              data-testid="amenity-terms-input"
              onChange={(event) =>
                setForm({ ...form, terms: event.target.value })
              }
              placeholder="Terms and resident rules"
              value={form.terms}
            />
            <Textarea
              className="mt-3"
              data-testid="amenity-instructions-input"
              onChange={(event) =>
                setForm({ ...form, instructions: event.target.value })
              }
              placeholder="Resident booking instructions"
              value={form.instructions}
            />
            <div
              className="mt-4 grid gap-3 border-t pt-4 sm:grid-cols-2"
              data-testid="amenity-future-fee-metadata"
            >
              <label className="flex items-center gap-2 text-sm">
                <input
                  checked={Boolean(form.fee_metadata?.fee_applicable)}
                  data-testid="amenity-fee-applicable-toggle"
                  onChange={(event) =>
                    setForm({
                      ...form,
                      fee_metadata: {
                        ...form.fee_metadata,
                        fee_applicable: event.target.checked,
                      },
                    })
                  }
                  type="checkbox"
                />
                Fee applicable later
              </label>
              <Input
                data-testid="amenity-fee-amount-input"
                disabled={!form.fee_metadata?.fee_applicable}
                min="0"
                onChange={(event) =>
                  setForm({
                    ...form,
                    fee_metadata: {
                      ...form.fee_metadata,
                      fee_amount: event.target.value,
                    },
                  })
                }
                placeholder="Future fee amount"
                type="number"
                value={form.fee_metadata?.fee_amount || ""}
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  checked={Boolean(form.fee_metadata?.deposit_applicable)}
                  data-testid="amenity-deposit-applicable-toggle"
                  onChange={(event) =>
                    setForm({
                      ...form,
                      fee_metadata: {
                        ...form.fee_metadata,
                        deposit_applicable: event.target.checked,
                      },
                    })
                  }
                  type="checkbox"
                />
                Deposit applicable later
              </label>
              <Input
                data-testid="amenity-deposit-amount-input"
                disabled={!form.fee_metadata?.deposit_applicable}
                min="0"
                onChange={(event) =>
                  setForm({
                    ...form,
                    fee_metadata: {
                      ...form.fee_metadata,
                      deposit_amount: event.target.value,
                    },
                  })
                }
                placeholder="Future deposit amount"
                type="number"
                value={form.fee_metadata?.deposit_amount || ""}
              />
            </div>
            <div className="mt-4 flex flex-wrap gap-4 text-sm">
              {[
                ["active", "Active"],
                ["resident_visible", "Resident-visible"],
                ["booking_enabled", "Booking enabled"],
                ["approval_required", "Approval required"],
              ].map(([key, label]) => (
                <label className="flex items-center gap-2" key={key}>
                  <input
                    checked={form[key]}
                    data-testid={`amenity-${key}-toggle`}
                    onChange={(event) =>
                      setForm({ ...form, [key]: event.target.checked })
                    }
                    type="checkbox"
                  />
                  {label}
                </label>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-3 text-sm">
              {[
                "OWNER",
                "JOINT_OWNER",
                "TENANT",
                "FAMILY_MEMBER",
                "AUTHORIZED_OCCUPANT",
              ].map((role) => (
                <label className="flex items-center gap-2" key={role}>
                  <input
                    checked={form.rules.eligible_relationships.includes(role)}
                    data-testid={`amenity-eligibility-${role}`}
                    onChange={(event) =>
                      setRule(
                        "eligible_relationships",
                        event.target.checked
                          ? [...form.rules.eligible_relationships, role]
                          : form.rules.eligible_relationships.filter(
                              (item) => item !== role,
                            ),
                      )
                    }
                    type="checkbox"
                  />
                  {role.replaceAll("_", " ")}
                </label>
              ))}
            </div>
            <Button
              className="mt-5"
              data-testid="amenity-save-button"
              onClick={saveAmenity}
            >
              <Plus className="h-4 w-4" />
              {editing ? "Update amenity" : "Create amenity"}
            </Button>
          </div>
          <div
            className="overflow-x-auto border bg-white"
            data-testid="amenity-master-table"
          >
            <table className="min-w-[780px] w-full text-sm">
              <thead className="bg-slate-100 text-left text-xs text-slate-600">
                <tr>
                  <th className="p-3">Amenity</th>
                  <th className="p-3">Location</th>
                  <th className="p-3">Booking mode</th>
                  <th className="p-3">Visibility</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {data.amenities.length === 0 ? (
                  <tr>
                    <td className="p-8 text-center text-slate-500" colSpan="5">
                      No amenities configured.
                    </td>
                  </tr>
                ) : (
                  data.amenities.map((item) => (
                    <tr
                      className="border-t"
                      data-testid={`amenity-row-${item.amenity_id}`}
                      key={item.amenity_id}
                    >
                      <td className="p-3">
                        <b>{item.name}</b>
                        <div className="text-xs text-slate-500">
                          {item.code} · {item.category || "Other"}
                        </div>
                      </td>
                      <td className="p-3">{item.location || "—"}</td>
                      <td className="p-3">
                        {item.slot_style.replaceAll("_", " ")}
                        <div className="text-xs text-slate-500">
                          {item.approval_required ? "Approval" : "Instant"}
                        </div>
                      </td>
                      <td className="p-3">
                        {item.active &&
                        item.resident_visible &&
                        item.booking_enabled
                          ? "Available"
                          : "Hidden / disabled"}
                      </td>
                      <td className="p-3">
                        <Button
                          data-testid={`amenity-edit-${item.amenity_id}`}
                          onClick={() => {
                            setEditing(item.amenity_id);
                            setForm(item);
                          }}
                          size="sm"
                          variant="outline"
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
      {tab === "blocks" && (
        <section className="grid gap-6 lg:grid-cols-[410px_1fr]">
          <div className="border bg-white p-5" data-testid="amenity-block-form">
            <h2 className="font-display text-xl font-semibold">
              Block a period
            </h2>
            <div className="mt-4 grid gap-3">
              <select
                className="rounded-md border border-slate-300 px-3 py-2"
                data-testid="amenity-block-amenity-select"
                onChange={(event) =>
                  setBlock({ ...block, amenity_id: event.target.value })
                }
                value={block.amenity_id}
              >
                <option value="">Select amenity</option>
                {data.amenities.map((item) => (
                  <option key={item.amenity_id} value={item.amenity_id}>
                    {item.name}
                  </option>
                ))}
              </select>
              <Input
                data-testid="amenity-block-start"
                onChange={(event) =>
                  setBlock({ ...block, start_local: event.target.value })
                }
                type="datetime-local"
                value={block.start_local}
              />
              <Input
                data-testid="amenity-block-end"
                onChange={(event) =>
                  setBlock({ ...block, end_local: event.target.value })
                }
                type="datetime-local"
                value={block.end_local}
              />
              <Textarea
                data-testid="amenity-block-reason"
                onChange={(event) =>
                  setBlock({ ...block, reason: event.target.value })
                }
                placeholder="Maintenance, repair, Society event..."
                value={block.reason}
              />
              <Textarea
                data-testid="amenity-block-message"
                onChange={(event) =>
                  setBlock({ ...block, resident_message: event.target.value })
                }
                placeholder="Resident-visible message (optional)"
                value={block.resident_message}
              />
            </div>
            <Button
              className="mt-4"
              data-testid="amenity-block-save"
              onClick={saveBlock}
            >
              <Ban className="h-4 w-4" />
              Block period
            </Button>
          </div>
          <div className="space-y-3" data-testid="amenity-block-list">
            {data.blocks.length === 0 ? (
              <p className={EMPTY_STATE_CLASS}>No blocked periods.</p>
            ) : (
              data.blocks.map((item) => (
                <article
                  className="border bg-white p-4"
                  data-testid={`amenity-block-${item.block_id}`}
                  key={item.block_id}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <b>{item.amenity_name}</b>
                      <p className="mt-1 text-sm text-slate-600">
                        {item.start_local.replace("T", " ")} to{" "}
                        {item.end_local.replace("T", " ")}
                      </p>
                      <p className="mt-2 text-sm text-slate-600">
                        {item.reason}
                      </p>
                    </div>
                    <Button
                      data-testid={`amenity-block-remove-${item.block_id}`}
                      onClick={async () => {
                        await api.delete(
                          `/society-amenities/blocks/${item.block_id}`,
                        );
                        await load();
                      }}
                      size="sm"
                      variant="outline"
                    >
                      Remove
                    </Button>
                  </div>
                </article>
              ))
            )}
          </div>
        </section>
      )}
      {tab === "bookings" && (
        <section
          className="overflow-x-auto border bg-white"
          data-testid="amenity-bookings-table"
        >
          <table className="min-w-[960px] w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs text-slate-600">
              <tr>
                <th className="p-3">Booking</th>
                <th className="p-3">Amenity / unit</th>
                <th className="p-3">Time</th>
                <th className="p-3">Purpose</th>
                <th className="p-3">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {data.bookings.length === 0 ? (
                <tr>
                  <td className="p-8 text-center text-slate-500" colSpan="6">
                    No bookings yet.
                  </td>
                </tr>
              ) : (
                data.bookings.map((item) => (
                  <tr
                    className="border-t"
                    data-testid={`amenity-booking-row-${item.booking_id}`}
                    key={item.booking_id}
                  >
                    <td className="p-3 font-mono text-xs">
                      {item.booking_number}
                    </td>
                    <td className="p-3">
                      <b>{item.amenity_snapshot?.name}</b>
                      <div className="text-xs text-slate-500">
                        Unit {item.unit_snapshot?.unit_number} ·{" "}
                        {item.applicant_snapshot?.name}
                      </div>
                    </td>
                    <td className="p-3">
                      {item.start_local?.replace("T", " ")}
                      <div className="text-xs text-slate-500">
                        to {item.end_local?.replace("T", " ")}
                      </div>
                    </td>
                    <td className="p-3">{item.purpose}</td>
                    <td className="p-3">
                      <Status value={item.status} />
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        {item.status === "PENDING_APPROVAL" && (
                          <>
                            <Button
                              data-testid={`amenity-approve-${item.booking_id}`}
                              onClick={() => action(item.booking_id, "approve")}
                              size="sm"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                              Approve
                            </Button>
                            <Button
                              data-testid={`amenity-reject-${item.booking_id}`}
                              onClick={() => action(item.booking_id, "reject")}
                              size="sm"
                              variant="outline"
                            >
                              <XCircle className="h-4 w-4" />
                              Reject
                            </Button>
                          </>
                        )}
                        {["PENDING_APPROVAL", "CONFIRMED"].includes(
                          item.status,
                        ) && (
                          <Button
                            data-testid={`amenity-cancel-${item.booking_id}`}
                            onClick={() => action(item.booking_id, "cancel")}
                            size="sm"
                            variant="outline"
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>
      )}
      {tab === "reports" && (
        <section
          className="border bg-white p-5"
          data-testid="amenity-reports-panel"
        >
          <h2 className="font-display text-xl font-semibold">
            Booking register
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Review real booking usage or export the operational register.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a
              data-testid="amenity-report-view"
              href={`${API}/society-amenities/reports/register`}
              rel="noreferrer"
              target="_blank"
            >
              <Button variant="outline">View register</Button>
            </a>
            <a
              data-testid="amenity-report-csv"
              href={`${API}/society-amenities/reports/register?export=csv`}
            >
              <Button variant="outline">
                <FileDown className="h-4 w-4" />
                CSV
              </Button>
            </a>
          </div>
        </section>
      )}
    </div>
  );
}
